import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { 
    FiDollarSign, FiPlus, FiEye, FiTrash2, FiSearch,
    FiList, FiGrid, FiFile, FiFileText, FiCalendar,
    FiClock, FiCheckCircle, FiAlertCircle, FiX,
    FiEdit2, FiFilter, FiTag, FiUser, FiBookmark
} from 'react-icons/fi';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import PinModal from '../components/PinModal';

const Expenses = () => {
    const { user } = useAuth();
    const [expenses, setExpenses] = useState([]);
    const [filteredExpenses, setFilteredExpenses] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [showPinModal, setShowPinModal] = useState(false);
    const [selectedExpense, setSelectedExpense] = useState(null);
    const [expenseToDelete, setExpenseToDelete] = useState(null);
    const [stats, setStats] = useState({});
    const [formData, setFormData] = useState({
        category_id: '',
        description: '',
        amount: '',
        payment_method: 'cash',
        payment_date: new Date(),
        supplier_id: '',
        reference: '',
        notes: ''
    });

    const isAdmin = user?.role === 'admin';
    const canManage = isAdmin || user?.role === 'caissier';

    useEffect(() => {
        if (canManage) {
            fetchExpenses();
            fetchCategories();
            fetchStats();
        }
    }, [categoryFilter, statusFilter, startDate, endDate]);

    useEffect(() => {
        filterExpenses();
    }, [search, expenses]);

    const fetchExpenses = async () => {
        try {
            let url = '/expenses?';
            if (startDate) url += `&start_date=${startDate.toISOString().split('T')[0]}`;
            if (endDate) url += `&end_date=${endDate.toISOString().split('T')[0]}`;
            if (categoryFilter !== 'all') url += `&category_id=${categoryFilter}`;
            if (statusFilter !== 'all') url += `&status=${statusFilter}`;
            
            const response = await api.get(url);
            setExpenses(response.data);
            setFilteredExpenses(response.data);
        } catch (error) {
            toast.error('Erreur lors du chargement des dépenses');
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await api.get('/expenses/categories');
            setCategories(response.data);
        } catch (error) {
            console.error('Erreur chargement catégories:', error);
        }
    };

    const fetchStats = async () => {
        try {
            let url = '/expenses/stats/summary?';
            if (startDate) url += `&start_date=${startDate.toISOString().split('T')[0]}`;
            if (endDate) url += `&end_date=${endDate.toISOString().split('T')[0]}`;
            
            const response = await api.get(url);
            setStats(response.data);
        } catch (error) {
            console.error('Erreur stats:', error);
        }
    };

    const filterExpenses = () => {
        let filtered = [...expenses];
        if (search) {
            filtered = filtered.filter(exp => 
                exp.description?.toLowerCase().includes(search.toLowerCase()) ||
                exp.expense_number?.toLowerCase().includes(search.toLowerCase()) ||
                exp.supplier_name?.toLowerCase().includes(search.toLowerCase())
            );
        }
        setFilteredExpenses(filtered);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.description || !formData.amount || parseFloat(formData.amount) <= 0) {
            toast.error('Description et montant sont requis');
            return;
        }

        try {
            const submitData = {
                ...formData,
                payment_date: formData.payment_date ? formData.payment_date.toISOString().split('T')[0] : null
            };
            await api.post('/expenses', submitData);
            toast.success('Dépense créée avec succès');
            setShowModal(false);
            resetForm();
            fetchExpenses();
            fetchStats();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Erreur lors de la création');
        }
    };

    const handleDelete = async () => {
        if (!expenseToDelete) return;
        try {
            await api.delete(`/expenses/${expenseToDelete.id}`);
            toast.success('Dépense supprimée');
            setShowPinModal(false);
            setExpenseToDelete(null);
            fetchExpenses();
            fetchStats();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const resetForm = () => {
        setFormData({
            category_id: '',
            description: '',
            amount: '',
            payment_method: 'cash',
            payment_date: new Date(),
            supplier_id: '',
            reference: '',
            notes: ''
        });
    };

    const formatAmount = (amount) => {
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XAF' }).format(amount || 0);
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const getStatusBadge = (status) => {
        const colors = {
            pending: 'bg-yellow-100 text-yellow-800',
            approved: 'bg-blue-100 text-blue-800',
            paid: 'bg-green-100 text-green-800',
            cancelled: 'bg-red-100 text-red-800'
        };
        const labels = {
            pending: 'En attente',
            approved: 'Approuvé',
            paid: 'Payé',
            cancelled: 'Annulé'
        };
        return (
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100'}`}>
                {labels[status] || status}
            </span>
        );
    };

    const getCategoryIcon = (icon) => icon || '📌';

    if (!canManage) {
        return (
            <div className="text-center py-12">
                <FiAlertCircle className="mx-auto text-red-500" size={48} />
                <p className="text-gray-500 mt-2">Vous n'avez pas accès à cette page</p>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            </div>
        );
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">💳 Gestion des dépenses</h1>
                    <p className="text-sm text-gray-500">Suivez et gérez toutes vos dépenses</p>
                </div>
                <button
                    onClick={() => {
                        resetForm();
                        setShowModal(true);
                    }}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
                >
                    <FiPlus size={18} />
                    Nouvelle dépense
                </button>
            </div>

            {/* Statistiques */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="card text-center">
                    <p className="text-sm text-gray-500">Total dépenses</p>
                    <p className="text-2xl font-bold text-gray-800">{stats.total_expenses || 0}</p>
                </div>
                <div className="card text-center">
                    <p className="text-sm text-gray-500">Montant total</p>
                    <p className="text-2xl font-bold text-red-600">{formatAmount(stats.total_amount)}</p>
                </div>
                <div className="card text-center">
                    <p className="text-sm text-gray-500">Payé</p>
                    <p className="text-2xl font-bold text-green-600">{formatAmount(stats.total_paid)}</p>
                </div>
                <div className="card text-center">
                    <p className="text-sm text-gray-500">En attente</p>
                    <p className="text-2xl font-bold text-yellow-600">{stats.pending_count || 0}</p>
                </div>
            </div>

            {/* Filtres */}
            <div className="card mb-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="relative">
                        <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Rechercher..."
                            className="input-field pl-10"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <select
                        className="input-field"
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                    >
                        <option value="all">Toutes les catégories</option>
                        {categories.map(cat => (
                            <option key={cat.id} value={cat.id}>{getCategoryIcon(cat.icon)} {cat.name}</option>
                        ))}
                    </select>
                    <select
                        className="input-field"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="all">Tous les statuts</option>
                        <option value="pending">En attente</option>
                        <option value="approved">Approuvé</option>
                        <option value="paid">Payé</option>
                        <option value="cancelled">Annulé</option>
                    </select>
                    <div className="flex gap-2">
                        <DatePicker
                            placeholderText="Date début"
                            selected={startDate}
                            onChange={setStartDate}
                            className="input-field"
                            dateFormat="dd/MM/yyyy"
                            isClearable
                        />
                        <DatePicker
                            placeholderText="Date fin"
                            selected={endDate}
                            onChange={setEndDate}
                            className="input-field"
                            dateFormat="dd/MM/yyyy"
                            isClearable
                        />
                    </div>
                </div>
            </div>

            {/* Liste des dépenses */}
            <div className="card overflow-x-auto">
                <table className="w-full">
                    <thead className="border-b">
                        <tr className="text-left">
                            <th className="pb-3">N°</th>
                            <th className="pb-3">Catégorie</th>
                            <th className="pb-3">Description</th>
                            <th className="pb-3 text-right">Montant</th>
                            <th className="pb-3">Date</th>
                            <th className="pb-3 text-center">Statut</th>
                            <th className="pb-3 text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredExpenses.map((expense) => (
                            <tr key={expense.id} className="border-b hover:bg-gray-50 transition-colors">
                                <td className="py-3 font-mono text-sm">{expense.expense_number}</td>
                                <td className="py-3">
                                    <span className="flex items-center gap-1">
                                        <span>{getCategoryIcon(expense.category_icon)}</span>
                                        <span className="text-sm">{expense.category_name || 'Non catégorisé'}</span>
                                    </span>
                                </td>
                                <td className="py-3">{expense.description}</td>
                                <td className="py-3 text-right font-bold text-red-600">
                                    {formatAmount(expense.amount)}
                                </td>
                                <td className="py-3 text-sm">{formatDate(expense.payment_date || expense.created_at)}</td>
                                <td className="py-3 text-center">{getStatusBadge(expense.status)}</td>
                                <td className="py-3 text-center">
                                    <div className="flex justify-center gap-2">
                                        <button
                                            onClick={() => {
                                                setSelectedExpense(expense);
                                                setShowDetailModal(true);
                                            }}
                                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                                            title="Voir détails"
                                        >
                                            <FiEye size={16} />
                                        </button>
                                        {isAdmin && (
                                            <button
                                                onClick={() => {
                                                    setExpenseToDelete(expense);
                                                    setShowPinModal(true);
                                                }}
                                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                                title="Supprimer"
                                            >
                                                <FiTrash2 size={16} />
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {filteredExpenses.length === 0 && (
                    <div className="text-center py-12">
                        <FiDollarSign className="mx-auto text-gray-300" size={48} />
                        <p className="text-gray-500 mt-2">Aucune dépense trouvée</p>
                    </div>
                )}
            </div>

            {/* Modal Création */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
                    <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold">💳 Nouvelle dépense</h2>
                            <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                                <FiX size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                                    <input
                                        type="text"
                                        className="input-field"
                                        placeholder="Description de la dépense"
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        required
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Montant (FCFA) *</label>
                                    <input
                                        type="number"
                                        className="input-field"
                                        placeholder="0"
                                        value={formData.amount}
                                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                                        required
                                        min="0"
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
                                    <select
                                        className="input-field"
                                        value={formData.category_id}
                                        onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                                    >
                                        <option value="">Sélectionner une catégorie</option>
                                        {categories.map(cat => (
                                            <option key={cat.id} value={cat.id}>
                                                {getCategoryIcon(cat.icon)} {cat.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Fournisseur</label>
                                    <input
                                        type="text"
                                        className="input-field"
                                        placeholder="Nom du fournisseur"
                                        value={formData.supplier_id}
                                        onChange={(e) => setFormData({ ...formData, supplier_id: e.target.value })}
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Mode de paiement</label>
                                    <select
                                        className="input-field"
                                        value={formData.payment_method}
                                        onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                                    >
                                        <option value="cash">Espèces</option>
                                        <option value="mobile_money">Mobile Money</option>
                                        <option value="bank_transfer">Virement</option>
                                        <option value="check">Chèque</option>
                                    </select>
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Date de paiement</label>
                                    <DatePicker
                                        selected={formData.payment_date}
                                        onChange={(date) => setFormData({ ...formData, payment_date: date })}
                                        className="input-field"
                                        dateFormat="dd/MM/yyyy"
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Référence</label>
                                    <input
                                        type="text"
                                        className="input-field"
                                        placeholder="Numéro de référence"
                                        value={formData.reference}
                                        onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                                    />
                                </div>
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                                    <select
                                        className="input-field"
                                        value={formData.status || 'pending'}
                                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                    >
                                        <option value="pending">En attente</option>
                                        <option value="approved">Approuvé</option>
                                        <option value="paid">Payé</option>
                                        <option value="cancelled">Annulé</option>
                                    </select>
                                </div>
                            </div>

                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                                <textarea
                                    className="input-field"
                                    rows="3"
                                    placeholder="Notes supplémentaires"
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                />
                            </div>

                            <div className="flex gap-3">
                                <button type="button" onClick={() => setShowModal(false)} className="flex-1 btn-secondary">
                                    Annuler
                                </button>
                                <button type="submit" className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700">
                                    Créer
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal Détails */}
            {showDetailModal && selectedExpense && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
                    <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-semibold">Détails de la dépense</h2>
                            <button onClick={() => setShowDetailModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                                <FiX size={20} />
                            </button>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-4 mb-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm text-gray-500">Numéro</p>
                                    <p className="font-semibold">{selectedExpense.expense_number}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Statut</p>
                                    {getStatusBadge(selectedExpense.status)}
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Catégorie</p>
                                    <p>{getCategoryIcon(selectedExpense.category_icon)} {selectedExpense.category_name || 'Non catégorisé'}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Montant</p>
                                    <p className="text-xl font-bold text-red-600">{formatAmount(selectedExpense.amount)}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Date de paiement</p>
                                    <p>{formatDate(selectedExpense.payment_date)}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Mode de paiement</p>
                                    <p>{selectedExpense.payment_method}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Fournisseur</p>
                                    <p>{selectedExpense.supplier_name || '-'}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Référence</p>
                                    <p>{selectedExpense.reference || '-'}</p>
                                </div>
                                <div className="col-span-2">
                                    <p className="text-sm text-gray-500">Description</p>
                                    <p>{selectedExpense.description}</p>
                                </div>
                                {selectedExpense.notes && (
                                    <div className="col-span-2">
                                        <p className="text-sm text-gray-500">Notes</p>
                                        <p>{selectedExpense.notes}</p>
                                    </div>
                                )}
                                <div>
                                    <p className="text-sm text-gray-500">Créé par</p>
                                    <p>{selectedExpense.created_by_name || '-'}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500">Créé le</p>
                                    <p>{formatDate(selectedExpense.created_at)}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <PinModal
                isOpen={showPinModal}
                onClose={() => {
                    setShowPinModal(false);
                    setExpenseToDelete(null);
                }}
                onConfirm={handleDelete}
                title="Supprimer une dépense"
                message="Êtes-vous sûr de vouloir supprimer cette dépense ? Cette action est irréversible."
            />
        </div>
    );
};

export default Expenses;